import { Core, Decorative } from "@faire/design-tokens";
import cart from "@faire/web-api/routes/cart";
import { getWindowOrThrow } from "@faire/web/common/globals/getWindow";
import {
  DEPRECATED_backwardsCompatibleValues,
  useStrictLocalization,
} from "@faire/web/common/localization/localize";
import { CartEmpty } from "@faire/web/slate/icons/foundation/CartEmpty";
import { ShoppingBag } from "@faire/web/slate/icons/foundation/ShoppingBag";
import { getSpacing, Spacer } from "@faire/web/slate/spacing";
import { responsiveMediaQueries } from "@faire/web/slate/Theme/responsiveMediaQueries";
import {
  Typography,
  Typography as Typography2,
  TypographyStyles,
} from "@faire/web/slate/Typography";
import { useViewport } from "@faire/web/ui/hooks/useViewport";
import { StrictLocalMsg } from "@faire/web/ui/lib/localization";
import { observer } from "mobx-react";
import * as React from "react";
import { useEffect } from "react";
import styled, { css } from "styled-components";

import { IconWithMaybeDot } from "@faire/retailer/components/Header/icons/IconWithMaybeDot";
import { HeaderIconContainer } from "@faire/retailer/components/Header/styled";
import { useCartItems } from "@faire/retailer/hooks/cart/useCartItems";
import { useCartPrefetch } from "@faire/retailer/hooks/cart/useCartPrefetch";
import {
  TimingRecorder,
  TIMING_KEYS,
} from "@faire/retailer/lib/actionLatencyTools";
import { NotificationDot } from "@faire/retailer/packages/messenger/views/NotificationDot";
import { getSettingEnableRetailerNotification } from "@faire/retailer/settings/getSettingEnableRetailerNotification";
import {
  useAssignSettingChangeBagToCartCopyAndIcon,
  useSettingChangeBagToCartCopyAndIcon,
} from "@faire/retailer/settings/useSettingChangeBagToCartCopyAndIcon";
import { useSettingGlobalNavigationRedesign } from "@faire/retailer/settings/useSettingGlobalNavigationRedesign";

import { linkHoverColor } from "./Colors";
import { topNavEvent } from "./Events";
import { IconWithCountBadge } from "./icons/IconWithCountBadge";
import { HeaderLink } from "./styled";

const Container = styled.div`
  display: flex;
  ${HeaderLink} {
    padding: 0;
  }

  ${responsiveMediaQueries.canHover} {
    &:hover {
      svg {
        color: ${linkHoverColor};
      }
    }
  }
`;

const HeaderCartBase = (props: HeaderCart.IProps) => {
  const { className, dataTestId } = props;
  const { isDesktopAndAbove } = useViewport();
  const { totalBrandsInCartFormatted } = useCartItems();
  const prefetchCart = useCartPrefetch();

  const cartPageLink = cart();
  const onCartLinkMouseEnter = () => prefetchCart(cartPageLink);

  const brandCountTestId = `${dataTestId}BrandCount`;
  const handleCartClick = () => {
    TimingRecorder.get().recordTime(TIMING_KEYS.VIEW_CART);
    topNavEvent("cart");

    if (!isDesktopAndAbove) {
      getWindowOrThrow().scrollTo?.({
        top: 0,
        behavior: "smooth",
      });
    }
  };

  const shouldUseRedesignExperimentSpacing =
    useSettingGlobalNavigationRedesign();
  const changeBagToCart = useSettingChangeBagToCartCopyAndIcon();
  const assignSettingChangeBagToCartCopyAndIcon =
    useAssignSettingChangeBagToCartCopyAndIcon();

  useEffect(() => {
    assignSettingChangeBagToCartCopyAndIcon();
  }, [assignSettingChangeBagToCartCopyAndIcon]);

  const isBrandCountZero = totalBrandsInCartFormatted === "0";

  const showNotificationDot = isDesktopAndAbove && !isBrandCountZero;

  const mobileShoppingBag = getSettingEnableRetailerNotification() ? (
    <HeaderContainer
      $border={!isBrandCountZero}
      $isBrandCountZero={isBrandCountZero}
      $enabledNotificationsVariant={getSettingEnableRetailerNotification()}
    >
      <ShoppingBagIcon
        isBrandCountZero={isBrandCountZero}
        showNotificationDot={showNotificationDot}
      />
      {!isBrandCountZero ? (
        <Typography variant="paragraphSansRegular">
          {totalBrandsInCartFormatted}
        </Typography>
      ) : null}
    </HeaderContainer>
  ) : (
    <>
      <ShoppingBagIcon
        isBrandCountZero={isBrandCountZero}
        showNotificationDot={showNotificationDot}
      />
      {totalBrandsInCartFormatted && totalBrandsInCartFormatted !== "0" ? (
        <>
          <Spacer size={4} />
          <MobileAndTabletCartCountLink
            data-test-id={brandCountTestId}
            $brandCount={totalBrandsInCartFormatted}
          >
            {totalBrandsInCartFormatted}
          </MobileAndTabletCartCountLink>
        </>
      ) : null}
    </>
  );

  return (
    <Container className={className}>
      {isDesktopAndAbove ? (
        <>
          {getSettingEnableRetailerNotification() &&
          !shouldUseRedesignExperimentSpacing ? (
            <Spacer
              width={
                changeBagToCart
                  ? 10
                  : {
                      mobileAndAbove: 0,
                      desktopAndAbove: isBrandCountZero ? 18 : "2x",
                    }
              }
            />
          ) : null}
          <HeaderLink
            data-test-id={dataTestId}
            to={cartPageLink}
            onClick={handleCartClick}
            onMouseEnter={onCartLinkMouseEnter}
            style={
              shouldUseRedesignExperimentSpacing
                ? { height: "unset" }
                : undefined
            }
          >
            {changeBagToCart ? (
              <HeaderCartIcon
                totalBrandsInCartFormatted={totalBrandsInCartFormatted}
              />
            ) : (
              <HeaderContainer
                $border={!isBrandCountZero}
                $enabledNotificationsVariant={getSettingEnableRetailerNotification()}
              >
                <ShoppingBagIcon
                  isBrandCountZero={isBrandCountZero}
                  showNotificationDot={showNotificationDot}
                />
                <DesktopCartCountLink
                  data-test-id={brandCountTestId}
                  $hidden={isBrandCountZero}
                >
                  {totalBrandsInCartFormatted === "1" ? (
                    <StrictLocalMsg
                      defaultMessage="{brandCount} Brand"
                      values={DEPRECATED_backwardsCompatibleValues({
                        brandCount: totalBrandsInCartFormatted,
                      })}
                    />
                  ) : (
                    <StrictLocalMsg
                      defaultMessage="{brandCount} Brands"
                      values={DEPRECATED_backwardsCompatibleValues({
                        brandCount: totalBrandsInCartFormatted,
                      })}
                    />
                  )}
                </DesktopCartCountLink>
              </HeaderContainer>
            )}
          </HeaderLink>
        </>
      ) : (
        <HeaderLink
          data-test-id={dataTestId}
          onClick={handleCartClick}
          to={cart()}
        >
          {changeBagToCart ? (
            <HeaderCartIcon
              totalBrandsInCartFormatted={totalBrandsInCartFormatted}
            />
          ) : (
            mobileShoppingBag
          )}
        </HeaderLink>
      )}
    </Container>
  );
};

export const HeaderCart = observer(HeaderCartBase);

const ShoppingBagIcon: React.FC<{
  isBrandCountZero: boolean;
  showNotificationDot: boolean;
}> = ({ isBrandCountZero, showNotificationDot }) => {
  const enableRetailerNotification = getSettingEnableRetailerNotification();
  return (
    <ImproveHeaderIconContainer
      $notificationEnabledVariant={enableRetailerNotification}
    >
      {isBrandCountZero && enableRetailerNotification ? (
        <IconWithMaybeDot
          icon={
            <ShoppingBag
              color={Core.icon.default}
              fontSize="18px"
              strokeWidth={1}
            />
          }
        />
      ) : (
        <ShoppingBag
          color={Core.icon.default}
          fontSize={enableRetailerNotification ? "18px" : "16px"}
          strokeWidth={enableRetailerNotification ? 1 : 2}
        />
      )}

      {showNotificationDot && !enableRetailerNotification ? (
        <NotificationDot
          $right={-2}
          $top={10}
          $color={Decorative.shade.orange}
        />
      ) : null}
    </ImproveHeaderIconContainer>
  );
};

const HeaderCartIcon: React.FC<{
  totalBrandsInCartFormatted: string | undefined;
}> = ({ totalBrandsInCartFormatted }) => {
  const { strictLocalize } = useStrictLocalization();
  const brandCount =
    (totalBrandsInCartFormatted?.length ?? 0) <= 3
      ? totalBrandsInCartFormatted
      : strictLocalize({
          defaultMessage: "999+",
          description: {
            text: "Count shown on bag next to cart when there are more than 999 brands in cart. String cannot exceed 4 characters.",
            img: "https://cdn.faire.com/fastly/98b3ab0e39d495bc565e5e2b90a4d1b52c6c4fb327fd57d1a4c6e63c08747736.png",
          },
        });

  return (
    <ExpandingHeaderIconContainer>
      <IconWithCountBadge
        icon={<InvertedCart fontSize="20px" />}
        count={brandCount}
      />
    </ExpandingHeaderIconContainer>
  );
};

const ImproveHeaderIconContainer = styled(HeaderIconContainer)<{
  $notificationEnabledVariant?: boolean;
}>`
  justify-content: flex-end;
  ${({ $notificationEnabledVariant }) =>
    $notificationEnabledVariant
      ? css`
          width: 32px;
        `
      : css`
          width: 28px;
        `}
`;

const MobileAndTabletCartCountLink = styled.div<{
  $brandCount: string;
}>`
  ${TypographyStyles({
    color: Core.text.inverse,
    variant: "paragraphSansMedium",
  })}
  font-size: 12px;
  display: flex;
  background-color: ${Decorative.shade.orange};
  border-radius: 50px;
  min-width: 16px;
  height: 16px;
  ${({ $brandCount }) => ($brandCount.length > 1 ? "padding: 0 6px;" : null)}
  justify-content: center;
  align-items: center;
`;

const HeaderContainerCss = (
  enabledNotificationsVariant?: boolean,
  isBrandCountZero?: boolean
) => css`
  display: flex;
  border-radius: 40px;
  border: 1px solid
    ${enabledNotificationsVariant ? Core.border.subdued : Core.border.default};
  height: 40px;
  margin-left: ${enabledNotificationsVariant ? 0 : "8px"};
  padding: 10px 16px 10px 4px;
  justify-content: center;
  align-items: center;
  gap: 8px;

  ${responsiveMediaQueries.desktopAndAbove} {
    min-width: 115px;
    top: ${getSpacing(0)};
  }

  ${responsiveMediaQueries.mobile} {
    position: relative;
    top: ${isBrandCountZero ? "5px" : 0};
    z-index: 1;
  }

  &:hover {
    background-color: ${Core.surface.muted};
    border: 1px solid ${Core.border.subdued};
  }
`;

const HeaderContainer = styled.div<{
  $border: boolean;
  $isBrandCountZero?: boolean;
  $enabledNotificationsVariant?: boolean;
}>`
  ${({ $border, $isBrandCountZero, $enabledNotificationsVariant }) =>
    $border
      ? HeaderContainerCss($enabledNotificationsVariant, $isBrandCountZero)
      : null};
`;

const DesktopCartCountLink = styled(Typography2)<{ $hidden: boolean }>`
  &&& {
    display: ${({ $hidden }) => ($hidden ? "none" : undefined)};
    ${TypographyStyles({ variant: "paragraphSansRegular" })}
    &:hover {
      color: ${Core.text.hover};
    }
  }
`;

const InvertedCart = styled(CartEmpty)`
  transform: scaleX(-1);
`;

const ExpandingHeaderIconContainer = styled(HeaderIconContainer)`
  width: 100%;
`;

namespace HeaderCart {
  export interface IProps {
    className?: string;
    dataTestId: string;
  }
}
